import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faFilter,
  faSearch,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../services/Interceptor";
import { fetchGraduationYears } from "../services/LeaderboardService";
import { positions } from "../utils/PositionStateData";

const DEFAULT_FILTERS = {
  year: [],
  position: [],
};

const MultiSelectDropdown = ({
  label,
  options,
  selectedValues = [],
  onSelect,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.toString().toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isSelected = (option) => selectedValues.includes(option);

  const handleOptionClick = (option) => {
    const nextValues = isSelected(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];

    onSelect(nextValues);
  };

  const selectedLabel =
    selectedValues.length > 0 ? selectedValues.join(", ") : "Select...";

  return (
    <div ref={dropdownRef} className="relative w-full">
      <label className="block mb-1 text-sm font-medium text-gray-700">
        {label}
      </label>

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex justify-between items-center border px-4 py-2 rounded-md bg-white"
        disabled={isLoading}
      >
        <span className="truncate text-left">{selectedLabel}</span>
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
        )}
      </button>

      {isOpen && (
        <div className="absolute mt-2 w-full bg-white border rounded-md shadow-lg z-30">
          <div className="p-2 border-b">
            <div className="flex items-center border rounded px-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-1 text-sm outline-none"
              />
            </div>
          </div>

          <ul className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <li className="px-4 py-2 text-center">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              </li>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option}
                  onClick={() => handleOptionClick(option)}
                  className="flex justify-between items-center px-4 py-2 cursor-pointer hover:bg-gray-100"
                >
                  <span>{option}</span>
                  {isSelected(option) && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-blue-600 w-4 h-4"
                    />
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function AthleteCardFilters({
  value = DEFAULT_FILTERS,
  onApply,
  onChange,
  onClose,
}) {
  const apiInstance = useAxiosInstance();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLoadingYears, setIsLoadingYears] = useState(false);
  const [yearOptions, setYearOptions] = useState([]);
  const [filters, setFilters] = useState({
    year: value.year || [],
    position: value.position || [],
  });

  const positionOptions = positions.filter((position) => position !== "None");

  useEffect(() => {
    setFilters({
      year: value.year || [],
      position: value.position || [],
    });
  }, [value]);

  useEffect(() => {
    const loadYears = async () => {
      try {
        setIsLoadingYears(true);
        const years = await fetchGraduationYears(apiInstance);
        setYearOptions(years || []);
      } catch (error) {
        setYearOptions([]);
      } finally {
        setIsLoadingYears(false);
      }
    };

    loadYears();
  }, [apiInstance]);

  const emitChange = (nextFilters) => {
    if (onChange) {
      onChange(nextFilters);
    }
  };

  const handleFilterChange = (name, values) => {
    const nextFilters = {
      ...filters,
      [name]: values,
    };
    setFilters(nextFilters);
    emitChange(nextFilters);
  };

  const closePanel = (shouldNotifyClose = true) => {
    setIsPanelOpen(false);
    if (shouldNotifyClose && onClose) onClose();
  };

  const togglePanel = () => {
    setIsPanelOpen((prev) => {
      const next = !prev;
      if (!next && onClose) onClose();
      return next;
    });
  };

  const handleClear = () => {
    setFilters(DEFAULT_FILTERS);
    emitChange(DEFAULT_FILTERS);
  };

  const handleApply = () => {
    if (onApply) onApply(filters);
    closePanel(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="w-full bg-gray-200 border border-gray-300 rounded-md px-4 py-2.5 flex items-center justify-center gap-2 text-sm whitespace-nowrap"
        onClick={togglePanel}
      >
        <FontAwesomeIcon icon={isPanelOpen ? faXmark : faFilter} />
        {isPanelOpen ? "Close Filters" : "Filters"}
      </button>

      {isPanelOpen && (
        <div className="border border-gray-300 absolute right-0 mt-2 w-[min(20rem,85vw)] bg-white rounded-lg shadow space-y-4 z-20">
          <div className="flex items-center justify-between px-4 pt-4">
            <h2 className="text-lg font-normal">Filters</h2>
          </div>

          <div className="flex flex-col md:flex-row md:flex-wrap gap-6 px-4">
            <MultiSelectDropdown
              label="Graduation Year"
              options={yearOptions}
              selectedValues={filters.year}
              onSelect={(values) => handleFilterChange("year", values)}
              isLoading={isLoadingYears}
            />
            <MultiSelectDropdown
              label="Position"
              options={positionOptions}
              selectedValues={filters.position}
              onSelect={(values) => handleFilterChange("position", values)}
            />
          </div>

          <div className="bg-gray-100 p-4 py-3 rounded-b-lg">
            <div className="flex justify-end gap-4">
              <button
                type="button"
                className="px-4 py-2 border rounded bg-white text-sm"
                onClick={handleClear}
              >
                Clear All
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-500 text-white rounded text-sm"
                onClick={handleApply}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
